import { Component, DOCUMENT, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Meta } from '@angular/platform-browser';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';

import { AuthService } from '@igo2/auth';
import { AnalyticsService } from '@igo2/core/analytics';
import { DomUtils } from '@igo2/utils';

import { environment, isPartenaireEnv } from '@env';
import { delay, filter, first, of, switchMap } from 'rxjs';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet],
  template: `<router-outlet />`
})
export class AppComponent {
  private metaService = inject(Meta);
  private authService = inject(AuthService);
  private analyticsService = inject(AnalyticsService);
  private document = inject(DOCUMENT);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);

  constructor() {
    this.configureAnalytics();
    this.readDescriptionConfig();
    this.removeSplashScreen();
  }

  private configureAnalytics() {
    if (isPartenaireEnv(environment)) {
      this.authService.authenticate$
        .pipe(
          filter((authenticated) => authenticated != null),
          switchMap((authenticated) =>
            authenticated ? this.authService.getProfils() : of(null)
          ),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe((profils) => {
          const user = this.authService.user;

          user && profils
            ? this.analyticsService.setUser(user, profils.profils)
            : this.analyticsService.setUser(null);
        });
    } else {
      this.analyticsService.paq.push(['trackPageView']);
      this.analyticsService.paq.push(['enableLinkTracking']);
    }
  }

  private readDescriptionConfig() {
    const description = environment.description;
    if (description) {
      this.metaService.addTag({ name: 'description', content: description });
    }
  }

  private removeSplashScreen(): void {
    this.router.events
      .pipe(
        first((events) => events instanceof NavigationEnd),
        delay(500)
      )
      .subscribe(() => {
        this._removeSplashScreen();
      });
  }

  private _removeSplashScreen(): void {
    const intro = this.document.getElementById('splash-screen');
    if (!intro) {
      return;
    }
    intro.classList.add('is-destroying');

    const destroyingAnimationTime = 300;
    const stylesheet = this.document.getElementById('splash-screen-stylesheet');
    if (!stylesheet) {
      return;
    }

    setTimeout(() => {
      DomUtils.remove(intro);
      DomUtils.remove(stylesheet);
    }, destroyingAnimationTime);
  }
}
